import { useCallback, useEffect, useRef } from "react";
import { useData } from "@hooks/useData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { ID } from "@models";

type CategoryProfileHighlightsResponse = components["schemas"]["SnapCategoryHighlightsModel"];

const REQUEST_TYPE: keyof typeof apiConfig = "get category profile highlights";

export function mapCategoryProfileHighlights(
    response: Pick<CategoryProfileHighlightsResponse, "bullets">,
): string[] {
    return (response.bullets ?? [])
        .map(({ text }) => text?.trim() ?? "")
        .filter(text => text.length > 0);
}

async function getCategoryProfileHighlights(id: ID, signal?: AbortSignal): Promise<string[]> {
    try {
        const response: CategoryProfileHighlightsResponse = await http.get(REQUEST_TYPE, { id }, { signal });
        return mapCategoryProfileHighlights(response);
    } catch (error) {
        console.error("Fetch error:", REQUEST_TYPE, error);
        return [];
    }
}

export default function useCategoryProfileHighlights(id: ID) {
    const abortRef = useRef<AbortController | null>(null);

    const fetchFn = useCallback((categoryId: ID) => {
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;

        return getCategoryProfileHighlights(categoryId, controller.signal);
    }, []);

    useEffect(() => () => abortRef.current?.abort(), []);

    return useData<string[]>([], fetchFn, id);
}
